(() => {
  const qs = (scope, sel) => scope.querySelector(sel);

  const decodeImage = (img) => {
    if (!img) return;

    try {
      if (img.decode) img.decode();
    } catch (_) {}
  };

  const debounce = (fn, wait = 80) => {
    let timeout;

    return (...args) => {
      window.clearTimeout(timeout);
      timeout = window.setTimeout(() => fn(...args), wait);
    };
  };

  const setupCard = (card) => {
    if (!card || card.dataset.haPgrid2Initialized === 'true') return;

    const viewport = qs(card, '[data-ha-pgrid2-viewport]');
    const track = qs(card, '[data-ha-pgrid2-track]');
    const mediaLink = qs(card, '.ha-pgrid2-card__media-link');

    /*
      Keep support for the older indicator hook in case any existing markup
      elsewhere still uses it, but use the new progress hook for this version.
    */
    const indicator = qs(card, '[data-ha-pgrid2-indicator]');
    const progress = qs(card, '[data-ha-pgrid2-progress]');

    const prevBtn = qs(card, '[data-ha-pgrid2-prev]') || qs(card, '.ha-pgrid2-card__btn--prev');
    const nextBtn = qs(card, '[data-ha-pgrid2-next]') || qs(card, '.ha-pgrid2-card__btn--next');

    if (!viewport || !track || !mediaLink) return;

    const originalSlides = Array.from(track.children);
    const realTotal = originalSlides.length;

    if (realTotal < 2) {
      if (progress) {
        progress.style.setProperty('--ha-pgrid2-progress', '1');
      }

      card.dataset.haPgrid2Initialized = 'true';
      return;
    }

    const firstClone = originalSlides[0].cloneNode(true);
    const lastClone = originalSlides[realTotal - 1].cloneNode(true);

    firstClone.setAttribute('data-ha-pgrid2-clone', 'true');
    lastClone.setAttribute('data-ha-pgrid2-clone', 'true');

    firstClone.setAttribute('aria-hidden', 'true');
    lastClone.setAttribute('aria-hidden', 'true');

    track.insertBefore(lastClone, originalSlides[0]);
    track.appendChild(firstClone);

    const slides = Array.from(track.children);
    const maxIndex = slides.length - 1;

    let index = 1;
    let suppressClick = false;
    let touchStartX = 0;
    let touchStartY = 0;
    let touchDeltaX = 0;
    let touchDeltaY = 0;
    let touchActive = false;
    let scrollEndTimer = null;
    let isTeleporting = false;

    const getRealIndex = (internalIndex = index) => {
      if (internalIndex === 0) return realTotal - 1;
      if (internalIndex === maxIndex) return 0;
      return internalIndex - 1;
    };

    const getProgressScale = (internalIndex = index) => {
      return (getRealIndex(internalIndex) + 1) / realTotal;
    };

    const updateCarouselState = (forcedInternalIndex) => {
      const internal = typeof forcedInternalIndex === 'number' ? forcedInternalIndex : index;
      const realIndex = getRealIndex(internal);

      if (indicator) {
        indicator.style.setProperty('--ha-indicator-index', String(realIndex));
      }

      if (progress) {
        progress.style.setProperty('--ha-pgrid2-progress', String(getProgressScale(internal)));
      }
    };

    const getSlideLeft = (targetIndex) => {
      const slide = slides[targetIndex];
      return slide ? slide.offsetLeft : 0;
    };

    const getNearestIndex = () => {
      const currentLeft = viewport.scrollLeft;
      let nearest = 0;
      let nearestDistance = Infinity;

      slides.forEach((slide, i) => {
        const distance = Math.abs(slide.offsetLeft - currentLeft);

        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearest = i;
        }
      });

      return nearest;
    };

    const preloadNearby = () => {
      decodeImage(slides[index]?.querySelector('img'));
      decodeImage(slides[index - 1]?.querySelector('img'));
      decodeImage(slides[index + 1]?.querySelector('img'));
    };

    const jumpToIndex = (targetIndex) => {
      isTeleporting = true;
      index = targetIndex;

      viewport.scrollTo({
        left: getSlideLeft(targetIndex),
        behavior: 'auto'
      });

      updateCarouselState(index);
      preloadNearby();

      window.requestAnimationFrame(() => {
        isTeleporting = false;
      });
    };

    const scrollToIndex = (targetIndex, smooth = true) => {
      index = targetIndex;

      viewport.scrollTo({
        left: getSlideLeft(targetIndex),
        behavior: smooth ? 'smooth' : 'auto'
      });

      updateCarouselState(index);
      preloadNearby();
    };

    const normalizeLoopPosition = () => {
      if (index === 0) {
        jumpToIndex(realTotal);
        return;
      }

      if (index === maxIndex) {
        jumpToIndex(1);
      }
    };

    const settleFromScroll = () => {
      if (isTeleporting) return;

      index = getNearestIndex();
      updateCarouselState(index);
      preloadNearby();
      normalizeLoopPosition();
    };

    const onScroll = () => {
      if (isTeleporting) return;

      window.clearTimeout(scrollEndTimer);

      const nearest = getNearestIndex();
      updateCarouselState(nearest);

      scrollEndTimer = window.setTimeout(settleFromScroll, 90);
    };

    const goPrev = (e) => {
      e?.preventDefault();
      scrollToIndex(index - 1, true);
    };

    const goNext = (e) => {
      e?.preventDefault();
      scrollToIndex(index + 1, true);
    };

    prevBtn?.addEventListener('click', goPrev);
    nextBtn?.addEventListener('click', goNext);

    viewport.addEventListener('scroll', onScroll, { passive: true });

    card.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goPrev();
      }

      if (e.key === 'ArrowRight') {
        e.preventDefault();
        goNext();
      }
    });

    viewport.addEventListener(
      'touchstart',
      (e) => {
        if (!e.touches || e.touches.length !== 1) return;

        const touch = e.touches[0];

        touchStartX = touch.clientX;
        touchStartY = touch.clientY;
        touchDeltaX = 0;
        touchDeltaY = 0;
        touchActive = true;
      },
      { passive: true }
    );

    viewport.addEventListener(
      'touchmove',
      (e) => {
        if (!touchActive || !e.touches || e.touches.length !== 1) return;

        const touch = e.touches[0];

        touchDeltaX = touch.clientX - touchStartX;
        touchDeltaY = touch.clientY - touchStartY;
      },
      { passive: true }
    );

    viewport.addEventListener(
      'touchend',
      () => {
        if (!touchActive) return;

        const absX = Math.abs(touchDeltaX);
        const absY = Math.abs(touchDeltaY);
        const isHorizontalSwipe = absX > 32 && absX > absY * 1.2;

        if (isHorizontalSwipe) {
          suppressClick = true;

          /*
            Native mobile scroll-snap now handles the physical swipe.
            We only suppress the link click, then let onScroll/settleFromScroll
            update the index, progress bar, and loop position.
          */
          window.setTimeout(() => {
            suppressClick = false;
          }, 360);
        }

        touchActive = false;
        touchDeltaX = 0;
        touchDeltaY = 0;
      },
      { passive: true }
    );

    mediaLink.addEventListener('click', (e) => {
      if (!suppressClick) return;

      e.preventDefault();
      e.stopPropagation();
      suppressClick = false;
    });

    prevBtn?.addEventListener('mouseup', () => prevBtn.blur());
    nextBtn?.addEventListener('mouseup', () => nextBtn.blur());

    card.addEventListener('mouseleave', () => {
      prevBtn?.blur();
      nextBtn?.blur();
    });

    const handleResize = debounce(() => {
      jumpToIndex(index);
    }, 80);

    window.addEventListener('resize', handleResize);

    window.requestAnimationFrame(() => {
      jumpToIndex(1);
    });

    card.dataset.haPgrid2Initialized = 'true';
  };

  const initProductGridCards = (scope = document) => {
    scope
      .querySelectorAll('.ha-pgrid2-card.is-carousel')
      .forEach(setupCard);
  };

  const handleAjaxLoadMore = async (event) => {
    const link = event.target.closest('[data-ha-pgrid2-load-more-link]');
    if (!link) return;

    const loadMore = link.closest('[data-ha-pgrid2-load-more]');
    const section = link.closest('[data-ha-pgrid2-section]');
    const grid = section?.querySelector('[data-ha-pgrid2-grid]');

    if (!loadMore || !section || !grid) return;

    event.preventDefault();

    if (loadMore.classList.contains('is-loading')) return;

    const nextUrl = link.getAttribute('href');
    if (!nextUrl) return;

    loadMore.classList.add('is-loading');
    loadMore.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(nextUrl, {
        credentials: 'same-origin'
      });

      if (!response.ok) {
        window.location.href = nextUrl;
        return;
      }

      const html = await response.text();
      const nextDocument = new DOMParser().parseFromString(html, 'text/html');

      const nextSection = nextDocument.getElementById(section.id);
      const nextGrid = nextSection?.querySelector('[data-ha-pgrid2-grid]');
      const nextLoadMore = nextSection?.querySelector('[data-ha-pgrid2-load-more]');

      if (!nextGrid) {
        window.location.href = nextUrl;
        return;
      }

      const nextItems = Array.from(nextGrid.children);

      nextItems.forEach((item) => {
        grid.appendChild(item);
      });

      if (nextLoadMore) {
        loadMore.replaceWith(nextLoadMore);
      } else {
        loadMore.remove();
      }

      initProductGridCards(section);
    } catch (error) {
      window.location.href = nextUrl;
    } finally {
      const currentLoadMore = section.querySelector('[data-ha-pgrid2-load-more]');
      currentLoadMore?.classList.remove('is-loading');
      currentLoadMore?.removeAttribute('aria-busy');
    }
  };

  const init = (scope = document) => {
    initProductGridCards(scope);
  };

  window.HAProductGridV2 = window.HAProductGridV2 || {};
  window.HAProductGridV2.init = init;

  if (!window.HAProductGridV2.ajaxLoadMoreBound) {
    document.addEventListener('click', handleAjaxLoadMore);
    window.HAProductGridV2.ajaxLoadMoreBound = true;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
  });
})();